/**
 * The "SQL Server storage" of a dedicated DB, like Azure's "Configure SQL Server storage": three rows
 * (data, log, TempDB) of `count` identical disks each, flattened into the payload's role-tagged `disks`
 * (all data disks, then all log disks, then all tempdb disks). TempDB has disks only when its location
 * is 'disks'; on 'local' it stays on the VM's temporary drive. Also the SQL login rules, the volumes the
 * pipeline mounts and the disk-type / zone rule.
 *
 * Counts are capped while flattening (a count still being typed must never allocate a huge list); the
 * form schema and the strict payload schema are what refuse a bad value.
 *
 * Node-safe: relative imports only, no import.meta.
 */
import {
  DB_VOLUMES,
  DISK_SIZE_MAX_GB,
  DISK_SIZE_MIN_GB,
  DISK_TYPES,
  MAX_DISKS,
  RESERVED_SQL_LOGINS,
  ZONE_ONLY_DISK_TYPES,
} from '../../config/workwise'
import type { DbDiskRole, DbStorageFormValue, DiskType, PayloadDbDisk, TempDbLocation } from '../../types/workwise'
import { SQL_LOGIN_REGEX } from './patterns'

// ─── SQL login ───────────────────────────────────────────────────────────────

export const SQL_LOGIN_REQUIRED_MESSAGE = 'SQL login name is required'
export const SQL_LOGIN_FORMAT_MESSAGE = 'Use 3 to 32 characters: a letter first, then letters, digits or underscores'
export const SQL_LOGIN_RESERVED_MESSAGE = 'This login name is reserved: choose another one.'

/** True for sa, admin, administrator ... in any letter case. */
export function isReservedSqlLogin(login: string): boolean {
  return RESERVED_SQL_LOGINS.includes(login.trim().toLowerCase())
}

/** Why a SQL login cannot be used (as typed, trimmed), or null when it can. */
export function sqlLoginProblem(login: string): string | null {
  const value = (login ?? '').trim()
  if (value === '') return SQL_LOGIN_REQUIRED_MESSAGE
  if (!SQL_LOGIN_REGEX.test(value)) return SQL_LOGIN_FORMAT_MESSAGE
  if (isReservedSqlLogin(value)) return SQL_LOGIN_RESERVED_MESSAGE
  return null
}

// ─── Disk groups ─────────────────────────────────────────────────────────────

export interface DbDiskGroup {
  role: DbDiskRole
  type: DiskType
  sizeGb: number
  count: number
}

/**
 * What the checks read: the form's storage, or the schema's parse of it, where a TempDB on the local
 * SSD may carry nothing but its location.
 */
export interface DbGroupLike {
  type: DiskType
  sizeGb?: number
  count?: number
}

export interface DbStorageLike {
  data: DbGroupLike
  log: DbGroupLike
  tempdb: { location: TempDbLocation } & Partial<DbGroupLike>
}

const NOT_A_NUMBER = Number.NaN

/** The rows that become disks: data, log, and TempDB only with dedicated disks. */
export function activeDbGroups(storage: DbStorageLike): DbDiskGroup[] {
  const { data, log, tempdb } = storage
  const groups: DbDiskGroup[] = [
    { role: 'data', type: data.type, sizeGb: data.sizeGb ?? NOT_A_NUMBER, count: data.count ?? NOT_A_NUMBER },
    { role: 'log', type: log.type, sizeGb: log.sizeGb ?? NOT_A_NUMBER, count: log.count ?? NOT_A_NUMBER },
  ]
  if (tempdb.location === 'disks') {
    groups.push({
      role: 'tempdb',
      type: tempdb.type as DiskType,
      sizeGb: tempdb.sizeGb ?? NOT_A_NUMBER,
      count: tempdb.count ?? NOT_A_NUMBER,
    })
  }
  return groups
}

/** A count as a number of disks: a positive whole number, at most one more than the cap, else 0. */
function diskCountOf(count: unknown): number {
  const n = Number(count)
  return Number.isInteger(n) && n > 0 ? Math.min(n, MAX_DISKS + 1) : 0
}

/** True when a row is complete and within the limits (type, size 4..32767, count 1..16). */
export function isValidDbGroup(group: Pick<DbDiskGroup, 'type' | 'sizeGb' | 'count'>): boolean {
  const sizeGb = Number(group.sizeGb)
  const count = Number(group.count)
  return (
    DISK_TYPES.includes(group.type) &&
    Number.isInteger(sizeGb) &&
    sizeGb >= DISK_SIZE_MIN_GB &&
    sizeGb <= DISK_SIZE_MAX_GB &&
    Number.isInteger(count) &&
    count >= 1 &&
    count <= MAX_DISKS
  )
}

function expand(groups: readonly DbDiskGroup[]): PayloadDbDisk[] {
  return groups.flatMap((group) =>
    Array.from({ length: diskCountOf(group.count) }, () => ({ sizeGb: Number(group.sizeGb), type: group.type, role: group.role })),
  )
}

/**
 * The payload's `disks`: every data disk, then every log disk, then every tempdb disk (only when TempDB
 * has dedicated disks), each row repeated `count` times with its type and size.
 */
export function flattenDbDisks(storage: DbStorageLike): PayloadDbDisk[] {
  return expand(activeDbGroups(storage))
}

/**
 * The same list for the cost estimate: a row that is not valid yet (still being typed) is left out and
 * the list never exceeds the 16 disks the backend accepts.
 */
export function pricedDbDisks(storage: DbStorageLike | undefined): PayloadDbDisk[] {
  if (!storage) return []
  return expand(activeDbGroups(storage).filter(isValidDbGroup)).slice(0, MAX_DISKS)
}

/** How many disks the DB gets in total (data + log + tempdb); a count that is not a whole number counts as 0. */
export function dbDiskCount(storage: DbStorageLike): number {
  return activeDbGroups(storage).reduce((sum, group) => sum + diskCountOf(group.count), 0)
}

/** True when every active row has a whole `count` of 1..16 (so the total can be judged). */
export function dbCountsInRange(storage: DbStorageLike): boolean {
  return activeDbGroups(storage).every((group) => {
    const n = Number(group.count)
    return Number.isInteger(n) && n >= 1 && n <= MAX_DISKS
  })
}

/** The role of the disk at `index` of the flattened list, or null when there is none. */
export function dbDiskRoleAt(storage: DbStorageLike, index: number): DbDiskRole | null {
  return flattenDbDisks(storage)[index]?.role ?? null
}

/** Roles whose disks need an availability zone (Premium SSD v2 or Ultra), in data, log, tempdb order. */
export function zoneOnlyRoles(storage: DbStorageLike): DbDiskRole[] {
  return activeDbGroups(storage)
    .filter((group) => ZONE_ONLY_DISK_TYPES.includes(group.type))
    .map((group) => group.role)
}

export const ZONE_REQUIRED_MESSAGE = 'Premium SSD v2 and Ultra disks need an availability zone: choose a zone or another disk type.'

// ─── What the user sees ──────────────────────────────────────────────────────

/** Where TempDB is mounted. */
export function tempDbVolume(storage: DbStorageLike): string {
  return storage.tempdb.location === 'disks' ? DB_VOLUMES.tempdb : DB_VOLUMES.tempdbLocal
}

/** "Volumes: data F:\data, log G:\log, tempdb D:\tempDb (local)". */
export function dbVolumeSummary(storage: DbStorageLike): string {
  const tempdb = `tempdb ${tempDbVolume(storage)}${storage.tempdb.location === 'disks' ? '' : ' (local)'}`
  return `Volumes: data ${DB_VOLUMES.data}, log ${DB_VOLUMES.log}, ${tempdb}`
}

/** "2 disks in total (16 at most)". */
export function dbDiskTotalText(storage: DbStorageLike): string {
  const total = dbDiskCount(storage)
  return `${total} ${total === 1 ? 'disk' : 'disks'} in total (${MAX_DISKS} at most)`
}

/** "2 x 512 GB Premium_LRS", or "—" for a row that is not complete. */
export function dbGroupText(group: Pick<DbDiskGroup, 'type' | 'sizeGb' | 'count'>): string {
  return isValidDbGroup(group) ? `${group.count} × ${group.sizeGb} GB ${group.type}` : '—'
}

/** One line per row for the review: "1 × 512 GB Premium_LRS (F:\data)" and TempDB "Local SSD (D:\tempDb)". */
export function dbRoleText(storage: DbStorageFormValue, role: DbDiskRole): string {
  if (role === 'tempdb' && storage.tempdb.location !== 'disks') return `Local SSD (${DB_VOLUMES.tempdbLocal})`
  return `${dbGroupText(storage[role])} (${role === 'tempdb' ? DB_VOLUMES.tempdb : DB_VOLUMES[role]})`
}

/** Panel-level error path (the total number of disks): not an input, so nothing is "touched" there. */
export const DB_STORAGE_PANEL_PATH = 'dbServer.storage.total'

/** The message when data + log + tempdb exceed the cap. */
export function totalDisksMessage(total: number): string {
  return `At most ${MAX_DISKS} disks in total (data, log and TempDB together); this is ${total}.`
}
